/** Customer-facing pages that previously had hardcoded English (catalog, legal-adjacent, cookies, feedback, etc.). */
export const publicModuleTranslations = {
  en: {
    catalog_title: 'Browse products',
    catalog_subtitle: 'Filter by category or use the header search.',
    categories_heading: 'Categories',
    view_more_products: 'View more products',
    about_fallback_title: 'About Lewouhmart',
    about_fallback_subtitle:
      'Retail operations built around dependable fulfillment, transparent service, and secure checkout.',
    about_fallback_body: `Lewouhmart runs a multi-country retail platform—serving households and businesses across many product categories—with disciplined inventory, fulfillment, and customer-care practices.

We combine stock management, routing and dispatch workflows, and secure digital payments so timelines are predictable and customers see clear status from checkout through delivery.

We work with vetted suppliers and structured receiving, tracking, and quality checks across the network. Teams use operational tooling for accuracy, incident handling, and service recovery so issues are addressed consistently.

Our commitment is straightforward: reliable service, honest communication, and responsible handling of customer data and payments at every step of the order lifecycle.`,
    about_image_alt: 'About Lewouhmart',
    cookie_page_title: 'Cookie settings',
    cookie_page_intro:
      'Essential cookies are always enabled. You can customize optional categories below.',
    cookie_essential_title: 'Essential cookies',
    cookie_essential_desc: 'Required for login, security, and cart.',
    cookie_analytics_title: 'Analytics',
    cookie_analytics_desc: 'Help us improve performance and UX.',
    cookie_personalization_title: 'Personalization',
    cookie_personalization_desc: 'Remember display and preference choices.',
    cookie_marketing_title: 'Marketing',
    cookie_marketing_desc: 'Enable marketing cookies and campaign attribution.',
    cookie_save: 'Save settings',
    cookie_accept_all: 'Accept all',
    cookie_reject: 'Reject non-essential',
    cookie_saved: 'Cookie settings saved.',
    cookie_banner_title: 'Cookie preferences',
    cookie_banner_body:
      'We use essential cookies to keep your session and cart working. You can accept all, reject non-essential, or customize settings.',
    cookie_banner_analytics: 'Analytics',
    cookie_banner_personalization: 'Personalization',
    cookie_banner_marketing: 'Marketing',
    cookie_banner_accept: 'Accept all',
    cookie_banner_reject: 'Reject non-essential',
    cookie_banner_settings_btn: 'Cookie settings',
    cookie_banner_save_btn: 'Save settings',
    feedback_page_title: 'Feedback and complaints',
    feedback_page_intro:
      'Share your feedback or submit an order complaint. Our team will review and respond.',
    feedback_guest_hint:
      'If your account is suspended (or you can’t sign in), you can still send feedback using your email below.',
    feedback_type_label: 'Type',
    feedback_type_general: 'General feedback',
    feedback_type_complaint: 'Order complaint',
    feedback_complaint_signin_hint:
      'Order complaints require signing in so we can verify the order. Please switch to “General feedback” or sign in.',
    feedback_email: 'Email',
    feedback_email_hint: 'Use the email of your customer account so we can find you.',
    feedback_order: 'Order',
    feedback_order_placeholder: 'Select an order',
    feedback_subject: 'Subject',
    feedback_subject_ph: 'Short subject',
    feedback_message: 'Message',
    feedback_message_ph: 'Please provide details.',
    feedback_sending: 'Sending...',
    feedback_send: 'Send message',
    feedback_signin_instead: 'Sign in instead',
    feedback_history_title: 'My feedback history',
    feedback_history_intro: 'Track the review status of your submitted feedback and complaints.',
    feedback_history_loading: 'Loading your history...',
    feedback_history_empty: 'You have not submitted feedback yet.',
    feedback_note_label: 'Team note',
    feedback_no_subject: '(No subject)',
    feedback_order_prefix: 'Order:',
    feedback_status_feedback: 'FEEDBACK',
    feedback_status_complaint: 'COMPLAINT',
    feedback_status_new: 'NEW',
    feedback_status_in_review: 'IN_REVIEW',
    feedback_status_resolved: 'RESOLVED',
    feedback_status_rejected: 'REJECTED',
    feedback_submit_error: 'Unable to submit.',
    feedback_sent_success: 'Your message has been sent successfully.',
    recover_title: 'Recover account with code',
    recover_intro:
      'Use one of your saved one-time recovery codes when you cannot access your email.',
    recover_success: 'Account recovered. You can sign in with your new password now.',
    recover_failed: 'Account recovery failed.',
    recover_email_ph: 'Current account email',
    recover_code_ph: 'Recovery code (e.g. ABCD-EFGH-IJKL)',
    recover_new_email_ph: 'New email (optional)',
    recover_new_password_ph: 'New password',
    recover_recovering: 'Recovering...',
    recover_submit: 'Recover account',
    recover_back_signin: 'Back to sign in',
    geo_brand: 'Lewouhmart',
    geo_contact: 'Contact support',
    geo_title: 'Service unavailable in your region',
    geo_body:
      'Access to this website is restricted for your location. If you believe this is an error, please contact support.',
    geo_country: 'Detected country:',
    geo_unknown_country:
      'We could not reliably determine your country and blocked access by policy.',
    geo_footer_rights: '© {{year}} Lewouhmart. All rights reserved.',
    geo_legal: 'Legal',
    geo_feedback: 'Feedback',
    notfound_code: '404',
    notfound_title: 'Page not found',
    notfound_body: 'The page {{path}} doesn’t match any route on this site.',
    notfound_home: 'Go home',
    notfound_products: 'Browse products',
    fab_open_aria: 'Open feedback panel',
    fab_close_aria: 'Close feedback panel',
    fab_title: 'Send feedback',
    fab_subtitle: 'Share your feedback with us.',
    fab_email: 'Email',
    fab_subject: 'Subject',
    fab_message: 'Message',
    fab_placeholder_email: 'you@example.com',
    fab_placeholder_subject: 'Short subject',
    fab_placeholder_message: 'Write your feedback...',
    fab_close: 'Close',
    fab_send_aria: 'Send feedback',
    fab_sending_aria: 'Sending feedback',
    fab_success: 'Thanks! Your feedback has been sent.',
    fab_error_generic: 'Unable to submit feedback.',
  },
  fr: {
    catalog_title: 'Parcourir les produits',
    catalog_subtitle: 'Filtrez par catégorie ou utilisez la recherche.',
    categories_heading: 'Catégories',
    view_more_products: 'Voir plus de produits',
    about_fallback_title: 'À propos de Lewouhmart',
    about_fallback_subtitle:
      'Une activité retail fondée sur une logistique fiable, un service transparent et des paiements sécurisés.',
    about_fallback_body: `Lewouhmart exploite une plateforme retail multinationale au service des particuliers et des entreprises, sur de nombreuses catégories de produits, avec des pratiques rigoureuses en stocks, logistique et relation client.

Nous combinons gestion des stocks, acheminement et expédition, et paiements numériques sécurisés pour des délais prévisibles et un suivi clair de la commande jusqu’à la livraison.

Nous travaillons avec des fournisseurs validés et des contrôles structurés à la réception, au traçage et à la qualité sur tout le réseau. Les équipes s’appuient sur des outils opérationnels pour la précision, la gestion des incidents et la récupération de service.

Notre engagement est simple : un service fiable, une communication transparente et un traitement responsable des données clients et des paiements à chaque étape du cycle de commande.`,
    about_image_alt: 'À propos de Lewouhmart',
    cookie_page_title: 'Paramètres des cookies',
    cookie_page_intro:
      'Les cookies essentiels sont toujours activés. Vous pouvez personnaliser les catégories optionnelles ci-dessous.',
    cookie_essential_title: 'Cookies essentiels',
    cookie_essential_desc: 'Nécessaires pour la connexion, la sécurité et le panier.',
    cookie_analytics_title: 'Analytique',
    cookie_analytics_desc: 'Nous aider à améliorer les performances et l’expérience.',
    cookie_personalization_title: 'Personnalisation',
    cookie_personalization_desc: 'Mémoriser l’affichage et les préférences.',
    cookie_marketing_title: 'Marketing',
    cookie_marketing_desc: 'Activer les cookies marketing et l’attribution des campagnes.',
    cookie_save: 'Enregistrer',
    cookie_accept_all: 'Tout accepter',
    cookie_reject: 'Refuser le non essentiel',
    cookie_saved: 'Paramètres enregistrés.',
    cookie_banner_title: 'Préférences cookies',
    cookie_banner_body:
      'Nous utilisons des cookies essentiels pour la session et le panier. Acceptez tout, refusez le non essentiel ou personnalisez.',
    cookie_banner_analytics: 'Analytique',
    cookie_banner_personalization: 'Personnalisation',
    cookie_banner_marketing: 'Marketing',
    cookie_banner_accept: 'Tout accepter',
    cookie_banner_reject: 'Refuser le non essentiel',
    cookie_banner_settings_btn: 'Paramètres cookies',
    cookie_banner_save_btn: 'Enregistrer',
    feedback_page_title: 'Avis et réclamations',
    feedback_page_intro:
      'Partagez votre avis ou signalez une commande. Notre équipe examinera et répondra.',
    feedback_guest_hint:
      'Si votre compte est suspendu (ou vous ne pouvez pas vous connecter), vous pouvez quand même écrire avec votre e-mail ci-dessous.',
    feedback_type_label: 'Type',
    feedback_type_general: 'Avis général',
    feedback_type_complaint: 'Réclamation commande',
    feedback_complaint_signin_hint:
      'Les réclamations nécessitent une connexion pour vérifier la commande. Choisissez « Avis général » ou connectez-vous.',
    feedback_email: 'E-mail',
    feedback_email_hint: 'Utilisez l’e-mail de votre compte client pour que nous vous retrouvions.',
    feedback_order: 'Commande',
    feedback_order_placeholder: 'Sélectionner une commande',
    feedback_subject: 'Objet',
    feedback_subject_ph: 'Objet court',
    feedback_message: 'Message',
    feedback_message_ph: 'Merci de détailler.',
    feedback_sending: 'Envoi...',
    feedback_send: 'Envoyer',
    feedback_signin_instead: 'Se connecter',
    feedback_history_title: 'Historique de mes messages',
    feedback_history_intro: 'Suivez l’état de vos avis et réclamations.',
    feedback_history_loading: 'Chargement...',
    feedback_history_empty: 'Vous n’avez pas encore envoyé de message.',
    feedback_note_label: 'Note de l’équipe',
    feedback_no_subject: '(Sans objet)',
    feedback_order_prefix: 'Commande :',
    feedback_status_feedback: 'AVIS',
    feedback_status_complaint: 'RÉCLAMATION',
    feedback_status_new: 'NOUVEAU',
    feedback_status_in_review: 'EN_COURS',
    feedback_status_resolved: 'RÉSOLU',
    feedback_status_rejected: 'REFUSÉ',
    feedback_submit_error: 'Envoi impossible.',
    feedback_sent_success: 'Votre message a bien été envoyé.',
    recover_title: 'Récupérer le compte avec un code',
    recover_intro:
      'Utilisez un code de récupération à usage unique si vous ne pouvez pas accéder à votre e-mail.',
    recover_success: 'Compte récupéré. Vous pouvez vous connecter avec votre nouveau mot de passe.',
    recover_failed: 'Échec de la récupération.',
    recover_email_ph: 'E-mail du compte actuel',
    recover_code_ph: 'Code de récupération (ex. ABCD-EFGH-IJKL)',
    recover_new_email_ph: 'Nouvel e-mail (facultatif)',
    recover_new_password_ph: 'Nouveau mot de passe',
    recover_recovering: 'Récupération...',
    recover_submit: 'Récupérer le compte',
    recover_back_signin: 'Retour à la connexion',
    geo_brand: 'Lewouhmart',
    geo_contact: 'Contacter le support',
    geo_title: 'Service indisponible dans votre région',
    geo_body:
      'L’accès à ce site est restreint pour votre localisation. Si vous pensez qu’il s’agit d’une erreur, contactez le support.',
    geo_country: 'Pays détecté :',
    geo_unknown_country:
      'Nous n’avons pas pu déterminer votre pays de façon fiable ; l’accès a été bloqué par politique.',
    geo_footer_rights: '© {{year}} Lewouhmart. Tous droits réservés.',
    geo_legal: 'Mentions légales',
    geo_feedback: 'Avis',
    notfound_code: '404',
    notfound_title: 'Page introuvable',
    notfound_body: 'La page {{path}} ne correspond à aucune route de ce site.',
    notfound_home: 'Accueil',
    notfound_products: 'Voir les produits',
    fab_open_aria: 'Ouvrir le panneau de feedback',
    fab_close_aria: 'Fermer le panneau de feedback',
    fab_title: 'Envoyer un avis',
    fab_subtitle: 'Partagez votre avis avec nous.',
    fab_email: 'E-mail',
    fab_subject: 'Objet',
    fab_message: 'Message',
    fab_placeholder_email: 'vous@exemple.com',
    fab_placeholder_subject: 'Objet court',
    fab_placeholder_message: 'Votre message...',
    fab_close: 'Fermer',
    fab_send_aria: 'Envoyer le feedback',
    fab_sending_aria: 'Envoi du feedback',
    fab_success: 'Merci ! Votre message a été envoyé.',
    fab_error_generic: 'Impossible d’envoyer le feedback.',
  },
  es: {
    catalog_title: 'Explorar productos',
    catalog_subtitle: 'Filtra por categoría o usa la búsqueda del encabezado.',
    categories_heading: 'Categorías',
    view_more_products: 'Ver más productos',
    about_fallback_title: 'Acerca de Lewouhmart',
    about_fallback_subtitle:
      'Operaciones retail centradas en logística confiable, servicio transparente y pago seguro.',
    about_fallback_body: `Lewouhmart opera una plataforma retail multinacional para hogares y empresas en muchas categorías de producto, con prácticas disciplinadas de inventario, cumplimiento y atención al cliente.

Combinamos gestión de stock, rutas y despacho y pagos digitales seguros para plazos previsibles y estado claro desde el checkout hasta la entrega.

Trabajamos con proveedores contrastados y controles estructurados de recepción, trazabilidad y calidad en toda la red. Los equipos usan herramientas operativas para precisión, incidencias y recuperación del servicio.

Nuestro compromiso es directo: servicio fiable, comunicación honesta y tratamiento responsable de datos y pagos en cada etapa del pedido.`,
    about_image_alt: 'Acerca de Lewouhmart',
    cookie_page_title: 'Configuración de cookies',
    cookie_page_intro:
      'Las cookies esenciales están siempre activas. Puedes personalizar las categorías opcionales abajo.',
    cookie_essential_title: 'Cookies esenciales',
    cookie_essential_desc: 'Necesarias para inicio de sesión, seguridad y carrito.',
    cookie_analytics_title: 'Analítica',
    cookie_analytics_desc: 'Ayúdanos a mejorar rendimiento y experiencia.',
    cookie_personalization_title: 'Personalización',
    cookie_personalization_desc: 'Recordar preferencias y visualización.',
    cookie_marketing_title: 'Marketing',
    cookie_marketing_desc: 'Activar cookies de marketing y atribución.',
    cookie_save: 'Guardar',
    cookie_accept_all: 'Aceptar todo',
    cookie_reject: 'Rechazar no esenciales',
    cookie_saved: 'Configuración guardada.',
    cookie_banner_title: 'Preferencias de cookies',
    cookie_banner_body:
      'Usamos cookies esenciales para sesión y carrito. Acepta todo, rechaza lo no esencial o personaliza.',
    cookie_banner_analytics: 'Analítica',
    cookie_banner_personalization: 'Personalización',
    cookie_banner_marketing: 'Marketing',
    cookie_banner_accept: 'Aceptar todo',
    cookie_banner_reject: 'Rechazar no esenciales',
    cookie_banner_settings_btn: 'Configuración de cookies',
    cookie_banner_save_btn: 'Guardar',
    feedback_page_title: 'Comentarios y reclamaciones',
    feedback_page_intro:
      'Envía comentarios o una reclamación de pedido. Nuestro equipo lo revisará y responderá.',
    feedback_guest_hint:
      'Si tu cuenta está suspendida (o no puedes iniciar sesión), aún puedes escribir con tu correo abajo.',
    feedback_type_label: 'Tipo',
    feedback_type_general: 'Comentario general',
    feedback_type_complaint: 'Reclamación de pedido',
    feedback_complaint_signin_hint:
      'Las reclamaciones requieren iniciar sesión para verificar el pedido. Elige «Comentario general» o inicia sesión.',
    feedback_email: 'Correo',
    feedback_email_hint: 'Usa el correo de tu cuenta de cliente para localizarte.',
    feedback_order: 'Pedido',
    feedback_order_placeholder: 'Selecciona un pedido',
    feedback_subject: 'Asunto',
    feedback_subject_ph: 'Asunto breve',
    feedback_message: 'Mensaje',
    feedback_message_ph: 'Por favor detalla.',
    feedback_sending: 'Enviando...',
    feedback_send: 'Enviar mensaje',
    feedback_signin_instead: 'Iniciar sesión',
    feedback_history_title: 'Historial de comentarios',
    feedback_history_intro: 'Sigue el estado de tus envíos.',
    feedback_history_loading: 'Cargando historial...',
    feedback_history_empty: 'Aún no has enviado comentarios.',
    feedback_note_label: 'Nota del equipo',
    feedback_no_subject: '(Sin asunto)',
    feedback_order_prefix: 'Pedido:',
    feedback_status_feedback: 'COMENTARIO',
    feedback_status_complaint: 'RECLAMACIÓN',
    feedback_status_new: 'NUEVO',
    feedback_status_in_review: 'EN_REVISION',
    feedback_status_resolved: 'RESUELTO',
    feedback_status_rejected: 'RECHAZADO',
    feedback_submit_error: 'No se pudo enviar.',
    feedback_sent_success: 'Tu mensaje se envió correctamente.',
    recover_title: 'Recuperar cuenta con código',
    recover_intro:
      'Usa uno de tus códigos de recuperación de un solo uso si no puedes acceder al correo.',
    recover_success: 'Cuenta recuperada. Ya puedes iniciar sesión con tu nueva contraseña.',
    recover_failed: 'Error al recuperar la cuenta.',
    recover_email_ph: 'Correo actual de la cuenta',
    recover_code_ph: 'Código de recuperación (p. ej. ABCD-EFGH-IJKL)',
    recover_new_email_ph: 'Nuevo correo (opcional)',
    recover_new_password_ph: 'Nueva contraseña',
    recover_recovering: 'Recuperando...',
    recover_submit: 'Recuperar cuenta',
    recover_back_signin: 'Volver al inicio de sesión',
    geo_brand: 'Lewouhmart',
    geo_contact: 'Contactar soporte',
    geo_title: 'Servicio no disponible en tu región',
    geo_body:
      'El acceso está restringido por tu ubicación. Si crees que es un error, contacta soporte.',
    geo_country: 'País detectado:',
    geo_unknown_country:
      'No pudimos determinar tu país de forma fiable y el acceso fue bloqueado por política.',
    geo_footer_rights: '© {{year}} Lewouhmart. Todos los derechos reservados.',
    geo_legal: 'Legal',
    geo_feedback: 'Comentarios',
    notfound_code: '404',
    notfound_title: 'Página no encontrada',
    notfound_body: 'La página {{path}} no coincide con ninguna ruta de este sitio.',
    notfound_home: 'Ir al inicio',
    notfound_products: 'Ver productos',
    fab_open_aria: 'Abrir panel de comentarios',
    fab_close_aria: 'Cerrar panel de comentarios',
    fab_title: 'Enviar comentario',
    fab_subtitle: 'Comparte tu opinión con nosotros.',
    fab_email: 'Correo',
    fab_subject: 'Asunto',
    fab_message: 'Mensaje',
    fab_placeholder_email: 'tu@ejemplo.com',
    fab_placeholder_subject: 'Asunto breve',
    fab_placeholder_message: 'Escribe tu comentario...',
    fab_close: 'Cerrar',
    fab_send_aria: 'Enviar comentario',
    fab_sending_aria: 'Enviando comentario',
    fab_success: '¡Gracias! Tu mensaje fue enviado.',
    fab_error_generic: 'No se pudo enviar el comentario.',
  },
  de: {
    catalog_title: 'Produkte durchsuchen',
    catalog_subtitle: 'Nach Kategorie filtern oder die Suche oben nutzen.',
    categories_heading: 'Kategorien',
    view_more_products: 'Weitere Produkte',
    about_fallback_title: 'Über Lewouhmart',
    about_fallback_subtitle:
      'Retail mit zuverlässiger Fulfillment-Logistik, transparentem Service und sicherer Kasse.',
    about_fallback_body: `Lewouhmart betreibt eine länderübergreifende Retail-Plattform für Haushalte und Unternehmen über viele Produktkategorien hinweg – mit disziplinierten Prozessen für Bestand, Fulfillment und Kundenbetreuung.

Wir verbinden Bestandsführung, Routing und Versand sowie sichere digitale Zahlungen, damit Zeitpläne planbar sind und Kunden vom Checkout bis zur Zustellung klare Statusinfos erhalten.

Wir arbeiten mit geprüften Lieferanten und strukturierten Prozessen bei Wareneingang, Nachverfolgung und Qualität im gesamten Netzwerk. Teams nutzen operative Tools für Genauigkeit, Incident-Management und Service-Wiederherstellung.

Unser Versprechen ist einfach: zuverlässiger Service, ehrliche Kommunikation und verantwortungsvoller Umgang mit Kundendaten und Zahlungen in jeder Phase des Bestelllebenszyklus.`,
    about_image_alt: 'Über Lewouhmart',
    cookie_page_title: 'Cookie-Einstellungen',
    cookie_page_intro:
      'Essenzielle Cookies sind immer aktiv. Optionale Kategorien können Sie unten anpassen.',
    cookie_essential_title: 'Essenzielle Cookies',
    cookie_essential_desc: 'Erforderlich für Login, Sicherheit und Warenkorb.',
    cookie_analytics_title: 'Analyse',
    cookie_analytics_desc: 'Helfen Sie uns, Leistung und UX zu verbessern.',
    cookie_personalization_title: 'Personalisierung',
    cookie_personalization_desc: 'Anzeige- und Präferenzen speichern.',
    cookie_marketing_title: 'Marketing',
    cookie_marketing_desc: 'Marketing-Cookies und Kampagnen-Attribution.',
    cookie_save: 'Speichern',
    cookie_accept_all: 'Alle akzeptieren',
    cookie_reject: 'Nicht essenzielle ablehnen',
    cookie_saved: 'Einstellungen gespeichert.',
    cookie_banner_title: 'Cookie-Präferenzen',
    cookie_banner_body:
      'Wir nutzen essenzielle Cookies für Sitzung und Warenkorb. Alle akzeptieren, nicht essenzielle ablehnen oder anpassen.',
    cookie_banner_analytics: 'Analyse',
    cookie_banner_personalization: 'Personalisierung',
    cookie_banner_marketing: 'Marketing',
    cookie_banner_accept: 'Alle akzeptieren',
    cookie_banner_reject: 'Nicht essenzielle ablehnen',
    cookie_banner_settings_btn: 'Cookie-Einstellungen',
    cookie_banner_save_btn: 'Speichern',
    feedback_page_title: 'Feedback und Beschwerden',
    feedback_page_intro:
      'Teilen Sie Feedback oder melden Sie eine Bestellbeschwerde. Unser Team prüft und antwortet.',
    feedback_guest_hint:
      'Wenn Ihr Konto gesperrt ist (oder Sie sich nicht anmelden können), können Sie trotzdem per E-Mail schreiben.',
    feedback_type_label: 'Art',
    feedback_type_general: 'Allgemeines Feedback',
    feedback_type_complaint: 'Bestellbeschwerde',
    feedback_complaint_signin_hint:
      'Beschwerden erfordern Anmeldung zur Bestellprüfung. Wählen Sie „Allgemeines Feedback“ oder melden Sie sich an.',
    feedback_email: 'E-Mail',
    feedback_email_hint: 'Nutzen Sie die E-Mail Ihres Kundenkontos.',
    feedback_order: 'Bestellung',
    feedback_order_placeholder: 'Bestellung wählen',
    feedback_subject: 'Betreff',
    feedback_subject_ph: 'Kurzer Betreff',
    feedback_message: 'Nachricht',
    feedback_message_ph: 'Bitte Details angeben.',
    feedback_sending: 'Senden...',
    feedback_send: 'Nachricht senden',
    feedback_signin_instead: 'Stattdessen anmelden',
    feedback_history_title: 'Mein Feedback-Verlauf',
    feedback_history_intro: 'Status Ihrer eingereichten Meldungen verfolgen.',
    feedback_history_loading: 'Verlauf wird geladen...',
    feedback_history_empty: 'Sie haben noch kein Feedback gesendet.',
    feedback_note_label: 'Team-Hinweis',
    feedback_no_subject: '(Ohne Betreff)',
    feedback_order_prefix: 'Bestellung:',
    feedback_status_feedback: 'FEEDBACK',
    feedback_status_complaint: 'BESCHWERDE',
    feedback_status_new: 'NEU',
    feedback_status_in_review: 'IN_PRÜFUNG',
    feedback_status_resolved: 'GELÖST',
    feedback_status_rejected: 'ABGELEHNT',
    feedback_submit_error: 'Senden nicht möglich.',
    feedback_sent_success: 'Ihre Nachricht wurde gesendet.',
    recover_title: 'Konto mit Code wiederherstellen',
    recover_intro:
      'Nutzen Sie einen Ihrer Einmal-Wiederherstellungscodes, wenn Sie keinen E-Mail-Zugang haben.',
    recover_success: 'Konto wiederhergestellt. Sie können sich mit dem neuen Passwort anmelden.',
    recover_failed: 'Wiederherstellung fehlgeschlagen.',
    recover_email_ph: 'Aktuelle Konto-E-Mail',
    recover_code_ph: 'Wiederherstellungscode (z. B. ABCD-EFGH-IJKL)',
    recover_new_email_ph: 'Neue E-Mail (optional)',
    recover_new_password_ph: 'Neues Passwort',
    recover_recovering: 'Wird wiederhergestellt...',
    recover_submit: 'Konto wiederherstellen',
    recover_back_signin: 'Zurück zur Anmeldung',
    geo_brand: 'Lewouhmart',
    geo_contact: 'Support kontaktieren',
    geo_title: 'Service in Ihrer Region nicht verfügbar',
    geo_body:
      'Der Zugriff ist für Ihren Standort eingeschränkt. Bei Irrtum wenden Sie sich an den Support.',
    geo_country: 'Erkanntes Land:',
    geo_unknown_country:
      'Land konnte nicht zuverlässig ermittelt werden; Zugriff wurde policybedingt blockiert.',
    geo_footer_rights: '© {{year}} Lewouhmart. Alle Rechte vorbehalten.',
    geo_legal: 'Rechtliches',
    geo_feedback: 'Feedback',
    notfound_code: '404',
    notfound_title: 'Seite nicht gefunden',
    notfound_body: 'Die Seite {{path}} existiert auf dieser Website nicht.',
    notfound_home: 'Zur Startseite',
    notfound_products: 'Produkte ansehen',
    fab_open_aria: 'Feedback-Panel öffnen',
    fab_close_aria: 'Feedback-Panel schließen',
    fab_title: 'Feedback senden',
    fab_subtitle: 'Teilen Sie uns Ihr Feedback mit.',
    fab_email: 'E-Mail',
    fab_subject: 'Betreff',
    fab_message: 'Nachricht',
    fab_placeholder_email: 'sie@beispiel.de',
    fab_placeholder_subject: 'Kurzer Betreff',
    fab_placeholder_message: 'Schreiben Sie Ihr Feedback...',
    fab_close: 'Schließen',
    fab_send_aria: 'Feedback senden',
    fab_sending_aria: 'Feedback wird gesendet',
    fab_success: 'Danke! Ihr Feedback wurde gesendet.',
    fab_error_generic: 'Feedback konnte nicht gesendet werden.',
  },
} as const;
